import { useEffect, useRef, useState, type Ref } from "react";
import { useSelector } from "react-redux";
import "./PatientHeader.scss";

import { RootState } from "../../../store/store";
import { getInitials } from "../../../utils/common";
import { AssessmentMode, useAssessmentProgress } from "../CoachAssessment/AssessmentProgressContext";

import { ReactComponent as ChevronDownIcon } from "../../../assets/icons/patient-layout-v2/header/ChevronDownIcon.svg";
import { ReactComponent as SettingsIcon } from "../../../assets/icons/patient-layout-v2/header/SettingsIcon.svg";
import { ReactComponent as PrefsIcon } from "../../../assets/icons/patient-layout-v2/header/PrefsIcon.svg";
import { ReactComponent as HelpIcon } from "../../../assets/icons/patient-layout-v2/header/HelpIcon.svg";
import { ReactComponent as SignOutIcon } from "../../../assets/icons/patient-layout-v2/header/SignOutIcon.svg";

const SETTINGS_ROUTE = "/settings";

// The portal runs inside the Angular app's iframe, which owns the session and
// the router — both actions below are handled there, not here.
const notifyParent = (message: Record<string, unknown>) =>
  window.parent.postMessage(message, "*");

const statusWord = (action: string) => {
  if (action === "published") return "Published";
  if (action === "preview") return "Preview";
  return "Draft";
};

interface PatientHeaderProps {
  titleSlotRef: Ref<HTMLDivElement>;
}

const PatientHeader = ({ titleSlotRef }: PatientHeaderProps) => {
  const [accountOpen, setAccountOpen] = useState(false);
  const [tpOpen, setTpOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);
  const tpRef = useRef<HTMLDivElement>(null);

  const { chrome } = useAssessmentProgress();

  const { healthCoachDetails } = useSelector(
    (state: RootState) => state.healthCoach,
  );
  const coach = healthCoachDetails?.data;

  const name =
    [coach?.first_name, coach?.last_name].filter(Boolean).join(" ").trim() ||
    "Health Coach";
  const initials = getInitials(name);
  const role = coach?.role || "";
  const email = coach?.email || "";

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
      }
      if (tpRef.current && !tpRef.current.contains(e.target as Node)) {
        setTpOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setTpOpen(false);
  }, [chrome?.activeSessionId]);

  const handleAccountSettings = () => {
    setAccountOpen(false);
    notifyParent({ messageType: "goToSettings", route: SETTINGS_ROUTE });
  };

  const handleSignOut = () => {
    setAccountOpen(false);
    notifyParent({ messageType: "signOut" });
  };

  const showChrome = Boolean(
    chrome && (chrome.sessions.length > 0 || chrome.canFollowUp),
  );
  const activeAction = chrome?.action || "draft";
  const isPublished = activeAction === "published";
  const statusLabel = chrome
    ? `${statusWord(activeAction)}${
        chrome.occurrence ? ` · ${chrome.occurrence}` : ""
      }`
    : "";

  return (
    <header className="topbar">
      <div className="topbar-inner">
        <div className="topbar-title" ref={titleSlotRef} />
        {showChrome && chrome && (
          <div className="topbar-chrome">
            {chrome.sessions.length > 0 ? (
            <div
              className={`tp-select ${tpOpen ? "open" : ""}`}
              id="timepoints"
              ref={tpRef}
            >
              <button
                type="button"
                className="tp-current"
                onClick={() => setTpOpen((p) => !p)}
              >
                <span className={`tp-dot2 ${activeAction}`} />
                <span>{chrome.occurrence || "Assessment"}</span>
                {chrome.activeDate ? (
                  <span className="tp-date">{chrome.activeDate}</span>
                ) : null}
                <svg
                  className="cc"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              {tpOpen && (
                <div className="tp-menu">
                  <div className="tp-menu-label">Assessments</div>
                  {chrome.sessions.map((s) => (
                    <button
                      key={s.sessionId}
                      type="button"
                      className={`tp-item ${
                        s.sessionId === chrome.activeSessionId ? "active" : ""
                      }`}
                      onClick={() => {
                        chrome.selectSession(s.sessionId);
                        setTpOpen(false);
                      }}
                    >
                      <span className={`tp-dot2 ${s.action}`} />
                      <span>{s.label}</span>
                      {s.action !== "published" ? (
                        <span className={`ti-src ${s.source}`}>
                          {s.source === "patient"
                            ? "Patient app"
                            : "Coach-filled"}
                        </span>
                      ) : null}
                      <span className="ti-meta">
                        {s.date ? <span className="ti-date">{s.date}</span> : null}
                        {s.canCancel ? (
                          <span
                            className="ti-del"
                            role="button"
                            title="Cancel this follow-up"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (
                                window.confirm(
                                  `Cancel "${s.label}"? This removes the visit and its data permanently.`,
                                )
                              ) {
                                chrome.cancelFollowUp(s.sessionId);
                                setTpOpen(false);
                              }
                            }}
                          >
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                            </svg>
                          </span>
                        ) : null}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            ) : null}

            {chrome.mode !== AssessmentMode.Baseline && (
            <button
              type="button"
              className={`tp-add ${chrome.canFollowUp ? "" : "disabled"}`}
              disabled={!chrome.canFollowUp || chrome.saving}
              onClick={() => chrome.newFollowUp()}
              title={
                chrome.canFollowUp
                  ? "Create a new follow-up assessment"
                  : "Publish the latest assessment before creating a new follow-up"
              }
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                width="13"
                height="13"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              New follow-up
            </button>
            )}

            {chrome.sessions.length > 0 ? (
              <>
            <span className={`status-pill ${activeAction}`}>
              <span className="sd" />
              {statusLabel}
            </span>

            {!isPublished ? (
              <span
                className={`src-pill ${
                  chrome.source === "coach" ? "coach" : "patient"
                }`}
                title={
                  chrome.source === "coach"
                    ? "This assessment was started / filled by the coach"
                    : "This assessment came from the patient app"
                }
              >
                {chrome.source === "coach"
                  ? "Coach-filled"
                  : "From patient app"}
              </span>
            ) : null}
              </>
            ) : null}
          </div>
        )}

        <div className="account" ref={accountRef}>
          <button
            className="account-btn"
            onClick={() => setAccountOpen((prev) => !prev)}
          >
            <span className="acc-av">{initials}</span>
            <span className="acc-meta">
              <span className="acc-name">{name}</span>
              {role && <span className="acc-role">{role}</span>}
            </span>
            <ChevronDownIcon className="cc" />
          </button>
          {accountOpen && (
            <div className="account-menu">
              <div className="acc-head">
                <span className="acc-av lg">{initials}</span>
                <div>
                  <div className="acc-h-name">{name}</div>
                  {role && <div className="acc-h-role">{role}</div>}
                  {email && <div className="acc-h-mail">{email}</div>}
                </div>
              </div>
              <div className="acc-note">
                Edits to the shared profile are recorded under this account.
              </div>
              <div className="acc-items">
                <button className="acc-item" onClick={handleAccountSettings}>
                  <SettingsIcon />
                  Account settings
                </button>
                <button className="acc-item" disabled>
                  <PrefsIcon />
                  Preferences
                  <span className="soon-tag">Soon</span>
                </button>
                <button className="acc-item" disabled>
                  <HelpIcon />
                  Help &amp; support
                  <span className="soon-tag">Soon</span>
                </button>
                <div className="acc-sep" />
                <button className="acc-item danger" onClick={handleSignOut}>
                  <SignOutIcon />
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default PatientHeader;
